import { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { editDepartment } from "../../services/departments/editDepartment";
import { getDepartmentById } from "../../services/departments/getDepartmentById";

const EditDepartmentForm = () => {
    const { can } = usePermissions();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [department, setDepartment] = useState({
        id: "",
        name: "",
        colorHex: "",
    });
    const canWriteDepartments = can("departments.write");
    const noPermissionMessage = "You don't have permission to do this.";

    const params = useParams();

    const getNewDepartment = async () => {
        const response = await getDepartmentById(params.id);

        if (response.success) {
            const d = response.data.department;

            setDepartment({
                ...d,
                // Normalize null to "" for inputs
                colorHex: d?.colorHex ? d.colorHex : "",
            });
        } else {
            if (response?.status === 403) return;
            toast.error(response.message);
        }
    };

    useEffect(() => {
        getNewDepartment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onHandleSubmitForm = async () => {
        if (!canWriteDepartments) return;

        if (department.name.trim() === "") {
            toast.error("Name can't be blank");
            return;
        }

        const payload = {
            name: department.name.trim(),
            colorHex: department.colorHex || null,
        };

        const response = await editDepartment(department.id, payload);

        if (response.success) {
            toast.success(response.data.message);
            // Keep UI in sync with what we sent
            setDepartment((prev) => ({
                ...prev,
                name: payload.name,
                colorHex: payload.colorHex || "",
            }));
        } else {
            toast.error(response.message);
        }
    };

    return (
        <section>
            <Toaster />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-2xl">
                                <Breadcrumbs
                                    home="/departments"
                                    pages={[
                                        {
                                            name: "Deparment",
                                            href: `/departments/create`,
                                            current: true,
                                        },
                                    ]}
                                />

                                <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                    <div className="mb-4 sm:mb-0">
                                        <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                            Edit Department
                                        </h1>
                                    </div>
                                </div>

                                <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold text-slate-800">
                                            Details
                                        </h2>
                                    </header>

                                    <div className="px-5 py-4 space-y-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="text"
                                                    onChange={(e) =>
                                                        setDepartment((prev) => ({
                                                            ...prev,
                                                            name: e.target.value,
                                                        }))
                                                    }
                                                    value={department.name}
                                                    className="w-full max-w-sm p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end w-full">
                                    <span
                                        data-tip={
                                            canWriteDepartments
                                                ? "Save"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            onClick={onHandleSubmitForm}
                                            disabled={!canWriteDepartments}
                                            className={`z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm focus:outline-none focus:ring-0 ${
                                                canWriteDepartments
                                                    ? "hover:bg-teal-600"
                                                    : "opacity-50 cursor-not-allowed"
                                            }`}
                                        >
                                            Save
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    );
};

export default EditDepartmentForm;
